import React, { useState } from 'react';
import { useNotify, useRefresh, useTranslate } from 'react-admin';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment,
  Stack, TextField, Tooltip, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import {
  EditRounded as EditIcon, DeleteOutlineRounded as DeleteIcon, VpnKeyOutlined as KeyIcon,
  ContentCopyRounded as CopyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { httpClient } from '@/utils/httpClient';
import { ACCOUNT_STATUS_TO_INT, LOGGING_STATUS_TO_INT } from '@/config/constants';
import { ConfirmDeleteDialog, RowActionsMenu } from '@/components/shell';
import { generateApiKey } from '@/features/servers/generateApiKey';
import type { AccountRecord } from './types';

/** Replaces the account's API key: the old one stops working at once, so the dialog says so first. */
const ChangeApiKeyDialog: React.FC<{ record: AccountRecord; open: boolean; onClose: () => void }> = ({
  record, open, onClose,
}) => {
  const [newApiKey, setNewApiKey] = useState(generateApiKey);
  const [saving, setSaving] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const currentKey = record.accountResponse?.apiKey || '';
  const copyLabel = t('accounts.copy_api_key');

  const handleSave = async () => {
    setSaving(true);
    try {
      const acc = record.accountResponse || {};
      await httpClient.put(`/admin/account/UpdateAccount/${record.id}`, {
        accountName: acc.accountName,
        apiKey: newApiKey,
        accountStatus: ACCOUNT_STATUS_TO_INT[acc.accountStatus ?? ''] ?? 1,
        loggingStatus: LOGGING_STATUS_TO_INT[acc.loggingStatus ?? ''] ?? 1,
        rateLimitApplies: acc.rateLimitApplies || false,
        whitelistApplies: false,
        rateLimitPerMin: acc.rateLimitPerMin || 0,
        rateLimitPerDay: acc.rateLimitPerDay || 0,
        primaryServerId: acc.primaryServer?.id,
        secondaryServerId: acc.secondaryServer?.id,
        licenseId: record.licenseResponse?.id,
        validityTimeFrame: record.validityTimeFrame,
        isLicenseBlocked: record.isLicenseBlocked || false,
      });
      notify(t('accounts.api_key_changed'), { type: 'success' });
      refresh();
    } catch {
      notify(t('ra.notification.http_error'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(newApiKey);
    notify(t('accounts.api_key_copied'));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isPhone} onClick={(e) => e.stopPropagation()}>
      <DialogTitle>{t('accounts.change_api_key')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity="warning">{t('accounts.api_key_warning')}</Alert>
          <Box>
            <Typography variant="body2" color="text.secondary">{t('accounts.current_key')}</Typography>
            <Typography variant="body2" sx={{ fontFamily: theme.custom.text.mono }}>
              {currentKey ? `${currentKey.substring(0, 8)}…` : t('accounts.no_api_key')}
            </Typography>
          </Box>
          <TextField
            label={t('accounts.new_key')}
            value={newApiKey}
            onChange={(e) => setNewApiKey(e.target.value)}
            fullWidth
            InputProps={{
              sx: { fontFamily: theme.custom.text.mono },
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={copyLabel}>
                    <IconButton onClick={handleCopy} aria-label={copyLabel} edge="end">
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Box>
            <Button variant="text" onClick={() => setNewApiKey(generateApiKey())}>
              {t('accounts.generate_new')}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose} disabled={saving}>{t('ra.action.cancel')}</Button>
        <Button variant="contained" color="error" onClick={handleSave} disabled={saving || !newApiKey}>
          {t('accounts.apply_new_key')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const AccountActions: React.FC<{ record: AccountRecord }> = ({ record }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [apiKeyOpen, setApiKeyOpen] = useState(false);

  return (
    <>
      <RowActionsMenu
        actions={[
          {
            key: 'edit',
            label: t('ra.action.edit'),
            icon: <EditIcon fontSize="small" />,
            onClick: () => navigate(`/accounts/${record.id}`),
          },
          {
            key: 'api-key',
            label: t('accounts.change_api_key'),
            icon: <KeyIcon fontSize="small" />,
            onClick: () => setApiKeyOpen(true),
          },
          {
            key: 'delete',
            label: t('ra.action.delete'),
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            dividerBefore: true,
            onClick: () => setDeleteOpen(true),
          },
        ]}
      />
      {/* Mounted only while open, so every opening offers a fresh key. */}
      {apiKeyOpen && <ChangeApiKeyDialog record={record} open onClose={() => setApiKeyOpen(false)} />}
      <ConfirmDeleteDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        name={record.accountResponse?.accountName || ''}
        onDelete={() => httpClient.delete(`/admin/account/DeleteAccount/${record.id}`)}
      />
    </>
  );
};
